import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { InfoTip } from "@/components/InfoTip";

const eur = (v) => `${v < 0 ? "−" : ""}${Math.abs(v).toFixed(2).replace(".", ",")} €`;
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const SETTINGS_KEY = "sux1_profit_settings";
const DEFAULTS = { rateServices: 21.2, rateGoods: 12.3, hosting: 9.2, domain: 1.5, acre: false };

const loadSettings = () => {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}") }; } catch { return DEFAULTS; }
};

// Admin: estimated monthly profit (sales − Stripe − URSSAF − AI − hosting).
export function AdminProfit() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(loadSettings);
  const period = `${year}-${String(month).padStart(2, "0")}`;

  useEffect(() => {
    api.get("/admin/profit", { params: { period } }).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [period]);
  useEffect(() => { localStorage.setItem(SETTINGS_KEY, JSON.stringify(cfg)); }, [cfg]);

  const num = (k) => (e) => setCfg({ ...cfg, [k]: Number(String(e.target.value).replace(",", ".")) || 0 });
  const factor = cfg.acre ? 0.5 : 1;
  const urssaf = d ? ((d.services_eur * cfg.rateServices + d.goods_eur * cfg.rateGoods) / 100) * factor : 0;
  const lines = d ? [
    ["Ventes SUX1 (packs)", d.services_eur, `${d.sales_count} vente(s)`],
    ["Ventes de marchandises", d.goods_eur, ""],
    ["Achats de marchandises", -d.purchases_eur, ""],
    ["Frais Stripe", -d.stripe_fees_eur, "1,5 % + 0,25 € par vente"],
    ["Cotisations URSSAF (estimées)", -urssaf, cfg.acre ? "avec ACRE (÷ 2)" : `${cfg.rateServices} % services · ${cfg.rateGoods} % marchandises`],
    ["IA — résumés des membres", -d.ai_members_eur, d.ai_by_model.map((m) => `${m.model} : ${m.count}`).join(" · ")],
    ["IA — essais gratuits", -d.ai_trials_eur, `${d.free_trials} essai(s)`],
    ["Hébergement Emergent", -cfg.hosting, "50 crédits / mois"],
    ["Nom de domaine", -cfg.domain, "≈ 18 € / an"],
  ] : [];
  const net = lines.reduce((s, l) => s + l[1], 0);
  const inp = "w-16 border border-border bg-background px-2 py-1 text-xs";
  const years = Array.from({ length: 3 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="border border-[#002FA7] p-6 max-w-3xl mt-6" data-testid="admin-profit-panel">
      <div className="sux-marker mb-2 flex items-center gap-2">
        <TrendingUp className="w-3 h-3" /> Rentabilité du mois
        <InfoTip testid="admin-profit-info" content={<><b>Estimation</b> : ventes réelles et frais Stripe exacts ; URSSAF selon vos taux ; coût IA estimé d'après la taille des textes résumés (questions de suivi et actions non comptées, quelques centimes). Le coût réel de l'IA et de l'hébergement est dans votre profil Emergent → Account Settings → Credit Usage.</>} />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <select className="border border-border bg-background px-3 py-2 text-sm" value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="admin-profit-month-select">
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select className="border border-border bg-background px-3 py-2 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="admin-profit-year-select">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {d && (
        <>
          <div className={`p-4 mb-4 text-white ${net >= 0 ? "bg-[#0a7a2f]" : "bg-[#FF2A00]"}`} data-testid="admin-profit-net">
            <div className="font-mono uppercase tracking-[0.2em] text-[10px] opacity-80">Bénéfice net estimé · {d.label}</div>
            <div className="text-3xl font-black mt-1" style={{ fontFamily: "Outfit" }}>{eur(net)}</div>
          </div>
          <table className="w-full text-sm mb-4" data-testid="admin-profit-table">
            <tbody>
              {lines.map(([label, v, note], i) => (
                <tr key={label} className="border-t border-border" data-testid={`admin-profit-row-${i}`}>
                  <td className="py-2 pr-2">{label}<div className="text-[11px] text-muted-foreground">{note}</div></td>
                  <td className={`py-2 text-right font-bold whitespace-nowrap ${v < 0 ? "text-[#FF2A00]" : ""}`}>{eur(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground" data-testid="admin-profit-settings">
        <label>URSSAF services % <input className={inp} value={cfg.rateServices} onChange={num("rateServices")} data-testid="admin-profit-rate-services" /></label>
        <label>marchandises % <input className={inp} value={cfg.rateGoods} onChange={num("rateGoods")} data-testid="admin-profit-rate-goods" /></label>
        <label>Hébergement € <input className={inp} value={cfg.hosting} onChange={num("hosting")} data-testid="admin-profit-hosting" /></label>
        <label>Domaine € <input className={inp} value={cfg.domain} onChange={num("domain")} data-testid="admin-profit-domain" /></label>
        <label className="flex items-center gap-1"><input type="checkbox" checked={cfg.acre} onChange={(e) => setCfg({ ...cfg, acre: e.target.checked })} data-testid="admin-profit-acre" /> ACRE</label>
      </div>
    </div>
  );
}

export default AdminProfit;
